/* eslint-disable max-lines */
import { RISK_LEVELS } from '@/lib/constants/risks'
import { TRAINING_STATUS } from '@/lib/constants/employees'
import { SIGNED_STATUS } from '@/lib/constants/policies'
import { USER_ROLES } from '@/lib/constants/users'
import { SEVERITIES } from '@/lib/constants/severities'
import type { IStatus } from '@/types/status'
import type { IUserRole } from '@/types/user'
import type { ISeverity } from '@/types/severity'
import type { IRiskLevel } from '@/types/risk'
import { NON_CONFORMITY_CATEGORIES } from '@/lib/constants/nonConformities'
import { HARDENING_POLICY_STATUS } from '@/lib/constants/hardening'
import { SUPPLIER_CATEGORIES, SUPPLIER_RISK_CLASSIFICATION } from './suppliers'
import { QUESTIONNAIRE_STATUS } from './status'
import type { IQuestionnaireStatus } from '@/types/supplierQuestionnaire'
// Legacy MUI palette (BRANDING_COLORS, USER_ROLES_COLORS, COLORS), goes away with MUI. Use THEME_COLORS or token classes
type IBrandingColor = {
  color: string
  backgroundColor: string
}
export const BRANDING_COLORS: Record<string, IBrandingColor> = {
  RED: { color: '#a32d2c', backgroundColor: '#fdeded' },
  RED_ACTIVE: { color: '#fdeded', backgroundColor: '#a32d2c' },
  YELLOW: { color: '#b28601', backgroundColor: '#fcf2d6ff' },
  YELLOW_ACTIVE: { color: '#fcf2d6ff', backgroundColor: '#b28601' },
  ORANGE: { color: '#ffa500', backgroundColor: '#ffe4c4' },
  GREEN: { color: '#3d9134', backgroundColor: '#e3f9e1ff' },
  GREEN_ACTIVE: { color: '#e3f9e1ff', backgroundColor: '#3d9134' },
  GRAY: { color: '#4A4A4A', backgroundColor: '#E5E5E5' },
  BLUE: { color: '#2d80b4', backgroundColor: '#e5f7feff' },
  BLUE_ACTIVE: { color: '#e5f7feff', backgroundColor: '#2d80b4' },
  PRIMARY: { color: '#2fa1a7', backgroundColor: '#e5f7feff' },
  // Monochromatic severity palette based on PRIMARY color (#2fa1a7)
  CRITICAL_TEAL: { color: '#082030', backgroundColor: '#a1cfe0' },
  HIGH_TEAL: { color: '#156570', backgroundColor: '#bcdde8' },
  MEDIUM_TEAL: { color: '#358890', backgroundColor: '#d7ebf0' },
  LOW_TEAL: { color: '#659fa5', backgroundColor: '#f2f9fa' },
  // Harmonious additional colors
  SUCCESS_GREEN: { color: '#22c55e', backgroundColor: '#f0fdf4' },
  WARNING_ORANGE: { color: '#f59e0b', backgroundColor: '#fffbeb' },
  INFO_BLUE: { color: '#3b82f6', backgroundColor: '#eff6ff' },
  NEUTRAL_GRAY: { color: '#64748b', backgroundColor: '#f8fafc' },
}
export const USER_ROLES_COLORS: Record<IUserRole, string> = {
  [USER_ROLES.VIEWER]: '#FFFFFF',
  [USER_ROLES.CONSULTANT]: '#4DA8E4',
  [USER_ROLES.EDITOR]: '#4FBF44',
  [USER_ROLES.CONSULTANT_ADMIN]: '#FFB52E',
  [USER_ROLES.ADMIN]: '#E066E0',
  [USER_ROLES.AUDITOR]: '#64748B',
  [USER_ROLES.MDM_EDITOR]: '#4FBF44',
  [USER_ROLES.SUPPLIER]: '#FFA500',
}

export const COLORS = {
  SIMULATED_ORG_COLOR: '#FFB52E',
  PRIMARY: {
    MAIN: '#2fa1a7',
    MAIN_NEW: '#3A7C92', // Same as globals.css
    LIGHT: '#81C4C7',
    DARK: '#0d4053',
    CONTRAST_TEXT: '#81c4c7',
  },
  GREY: {
    DARK: '#4A4A4A',
    LIGHT: '#E0E0E0',
    DEFAULT: '#6B7280',
  },
  SECONDARY: {
    MAIN: '#2fa1a7',
    LIGHT: '#ecf7fb',
    CONTRAST_TEXT: '#ecf7fb',
  },
  PROGRESS_BAR: {
    BACKGROUND: '#ecf7fb',
    COLOR: '#2fa1a7',
    ERROR: '#ab2315',
    WARNING: '#FFB52E',
  },
  DANGER: '#a32d2c',
  SUCCESS: '#3d9134',
  BACKGROUND: {
    DARK: '#e6e6e6',
    LIGHT: '#ffffff',
    DEFAULT: '#ffffff',
    SIDEBAR: '#faf8f8ff',
  },
  BORDER: {
    DEFAULT: '#e0e0e0',
  },
  CRITICALITY_LEVEL: {
    [SEVERITIES.LOW]: BRANDING_COLORS.LOW_TEAL,
    [SEVERITIES.MEDIUM]: BRANDING_COLORS.MEDIUM_TEAL,
    [SEVERITIES.HIGH]: BRANDING_COLORS.HIGH_TEAL,
    [SEVERITIES.CRITICAL]: BRANDING_COLORS.CRITICAL_TEAL,
  } satisfies Record<ISeverity, IBrandingColor>,
  TRAINING_COMPLETED: {
    [TRAINING_STATUS.COMPLETED]: BRANDING_COLORS.GREEN,
    [TRAINING_STATUS.NOT_COMPLETED]: BRANDING_COLORS.RED,
    [TRAINING_STATUS.PARTIALLY_COMPLETED]: BRANDING_COLORS.YELLOW,
    [TRAINING_STATUS.UNKNOWN]: BRANDING_COLORS.GRAY,
  },
  POLICIES_SIGNED: {
    [SIGNED_STATUS.SIGNED]: BRANDING_COLORS.GREEN,
    [SIGNED_STATUS.NOT_SIGNED]: BRANDING_COLORS.RED,
    [SIGNED_STATUS.PARTIALLY_SIGNED]: BRANDING_COLORS.YELLOW,
  },
  STATUS: {
    notStarted: BRANDING_COLORS.RED,
    completed: BRANDING_COLORS.BLUE,
    notApplicable: BRANDING_COLORS.GRAY,
    inProgress: BRANDING_COLORS.YELLOW,
    approved: BRANDING_COLORS.GREEN,
    planned: BRANDING_COLORS.GREEN,
    delayed: BRANDING_COLORS.YELLOW,
    closed: BRANDING_COLORS.RED,
    inReview: BRANDING_COLORS.ORANGE,
    open: BRANDING_COLORS.BLUE,
    muted: BRANDING_COLORS.YELLOW,
    error: BRANDING_COLORS.RED,
  } satisfies Record<IStatus, IBrandingColor>,
  CLUSTER_CHIP: BRANDING_COLORS.GRAY,
  STANDARD_CHIP: BRANDING_COLORS.PRIMARY,
  NOT_ONBOARDED_CHIP: BRANDING_COLORS.GRAY,
  ADMIN_CHIP: BRANDING_COLORS.YELLOW,
  PORT_STATUS: {
    open: BRANDING_COLORS.GREEN,
    closed: BRANDING_COLORS.RED,
    filtered: BRANDING_COLORS.YELLOW,
  },
  CERTIFICATE_STATUS: {
    valid: BRANDING_COLORS.GREEN,
    expired: BRANDING_COLORS.RED,
  },
  SEVERITY: {
    [SEVERITIES.CRITICAL]: BRANDING_COLORS.CRITICAL_TEAL,
    [SEVERITIES.HIGH]: BRANDING_COLORS.HIGH_TEAL,
    [SEVERITIES.MEDIUM]: BRANDING_COLORS.MEDIUM_TEAL,
    [SEVERITIES.LOW]: BRANDING_COLORS.LOW_TEAL,
  } satisfies Record<ISeverity, IBrandingColor>,
  SEVERITY_ALTERNATE_THEME: {
    [SEVERITIES.CRITICAL]: BRANDING_COLORS.RED,
    [SEVERITIES.HIGH]: BRANDING_COLORS.YELLOW,
    [SEVERITIES.MEDIUM]: BRANDING_COLORS.BLUE,
    [SEVERITIES.LOW]: BRANDING_COLORS.GREEN,
  } satisfies Record<ISeverity, IBrandingColor>,
  SEVERITY_ALTERNATE_THEME_ACTIVE: {
    [SEVERITIES.CRITICAL]: BRANDING_COLORS.RED_ACTIVE,
    [SEVERITIES.HIGH]: BRANDING_COLORS.YELLOW_ACTIVE,
    [SEVERITIES.MEDIUM]: BRANDING_COLORS.BLUE_ACTIVE,
    [SEVERITIES.LOW]: BRANDING_COLORS.GREEN_ACTIVE,
  } satisfies Record<ISeverity, IBrandingColor>,
  NON_CONFORMITY_CATEGORIES: {
    [NON_CONFORMITY_CATEGORIES.MAJOR]: BRANDING_COLORS.RED,
    [NON_CONFORMITY_CATEGORIES.MINOR]: BRANDING_COLORS.YELLOW,
    [NON_CONFORMITY_CATEGORIES.OBSERVATION]: BRANDING_COLORS.BLUE,
    [NON_CONFORMITY_CATEGORIES.OPPORTUNITY]: BRANDING_COLORS.GREEN,
  },
  CRITICALITY_FILTER_LEVEL: {
    [SEVERITIES.CRITICAL]: BRANDING_COLORS.CRITICAL_TEAL,
    [SEVERITIES.HIGH]: BRANDING_COLORS.HIGH_TEAL,
    [SEVERITIES.MEDIUM]: BRANDING_COLORS.MEDIUM_TEAL,
    [SEVERITIES.LOW]: BRANDING_COLORS.LOW_TEAL,
  } satisfies Record<ISeverity, IBrandingColor>,
  ROLE: {
    [USER_ROLES.ADMIN]: BRANDING_COLORS.YELLOW,
    [USER_ROLES.CONSULTANT]: BRANDING_COLORS.BLUE,
    [USER_ROLES.EDITOR]: BRANDING_COLORS.GREEN,
    [USER_ROLES.CONSULTANT_ADMIN]: BRANDING_COLORS.RED,
    [USER_ROLES.VIEWER]: BRANDING_COLORS.GRAY,
    [USER_ROLES.AUDITOR]: BRANDING_COLORS.NEUTRAL_GRAY,
    [USER_ROLES.MDM_EDITOR]: BRANDING_COLORS.GREEN,
    [USER_ROLES.SUPPLIER]: BRANDING_COLORS.ORANGE,
  } satisfies Record<IUserRole, IBrandingColor>,
  RISK_SCORE_CHIP: {
    CRITICAL: BRANDING_COLORS.RED,
    HIGH: BRANDING_COLORS.YELLOW,
    MEDIUM: BRANDING_COLORS.GREEN,
    LOW: BRANDING_COLORS.GRAY,
  },
  RISK_LEVELS: {
    [RISK_LEVELS.VERY_LOW]: BRANDING_COLORS.GRAY,
    [RISK_LEVELS.LOW]: BRANDING_COLORS.BLUE,
    [RISK_LEVELS.MEDIUM]: BRANDING_COLORS.GREEN,
    [RISK_LEVELS.HIGH]: BRANDING_COLORS.YELLOW,
    [RISK_LEVELS.VERY_HIGH]: BRANDING_COLORS.RED,
    [RISK_LEVELS.UNKNOWN]: BRANDING_COLORS.GRAY,
  } satisfies Record<IRiskLevel, IBrandingColor>,
  RISK_TREATMENT: {
    mitigate: BRANDING_COLORS.RED,
    accept: BRANDING_COLORS.GREEN,
  },
  BREACH_SEVERITY: {
    critical: BRANDING_COLORS.RED,
    high: BRANDING_COLORS.YELLOW,
    medium: BRANDING_COLORS.GREEN,
    low: BRANDING_COLORS.GRAY,
    unknown: BRANDING_COLORS.GRAY,
  },
  HARDENING_STATUS: {
    [HARDENING_POLICY_STATUS.PASSED]: BRANDING_COLORS.GREEN,
    [HARDENING_POLICY_STATUS.WARNING]: BRANDING_COLORS.YELLOW,
    [HARDENING_POLICY_STATUS.FAILED]: BRANDING_COLORS.RED,
    [HARDENING_POLICY_STATUS.NOT_APPLICABLE]: BRANDING_COLORS.GRAY,
  },
  SUPPLIER_RISK_LEVEL: {
    [SUPPLIER_RISK_CLASSIFICATION.VERY_LOW]: BRANDING_COLORS.GRAY,
    [SUPPLIER_RISK_CLASSIFICATION.LOW]: BRANDING_COLORS.BLUE,
    [SUPPLIER_RISK_CLASSIFICATION.MEDIUM]: BRANDING_COLORS.GREEN,
    [SUPPLIER_RISK_CLASSIFICATION.HIGH]: BRANDING_COLORS.YELLOW,
    [SUPPLIER_RISK_CLASSIFICATION.EXTREME]: BRANDING_COLORS.RED,
  },
  SUPPLIER_CATEGORY: {
    [SUPPLIER_CATEGORIES.CRITICAL]: BRANDING_COLORS.RED,
    [SUPPLIER_CATEGORIES.NON_CRITICAL]: BRANDING_COLORS.GREEN,
  },
  QUESTIONNAIRE_STATUS: {
    [QUESTIONNAIRE_STATUS.SUBMITTED]: BRANDING_COLORS.GRAY,
    [QUESTIONNAIRE_STATUS.IN_PROGRESS]: BRANDING_COLORS.YELLOW,
    [QUESTIONNAIRE_STATUS.READY]: BRANDING_COLORS.GREEN,
    [QUESTIONNAIRE_STATUS.COMPLETED]: BRANDING_COLORS.PRIMARY,
  } satisfies Record<IQuestionnaireStatus, IBrandingColor>,
}

// Tokens from globals.css, for props that take a color value instead of a class (SVG, charts)
export const THEME_COLORS = {
  PRIMARY: 'var(--qalea-primary)',
  PRIMARY_LIGHT: 'var(--qalea-primary-light)',
  PRIMARY_DARK: 'var(--qalea-primary-dark)',
  ICON_SUCCESS: 'var(--icon-success)',
  DOT_INACTIVE: 'var(--dot-inactive)',
} as const

export const CHART_COLORS = {
  PRIMARY: 'var(--chart-primary)',
  SUCCESS: 'var(--chart-success)',
  SUCCESS_BORDER: 'var(--chart-success-border)',
  GOOD: 'var(--chart-good)',
  GOOD_BORDER: 'var(--chart-good-border)',
  WARNING: 'var(--chart-warning)',
  WARNING_BORDER: 'var(--chart-warning-border)',
  ATTENTION: 'var(--chart-attention)',
  ATTENTION_BORDER: 'var(--chart-attention-border)',
  ERROR: 'var(--chart-danger)',
  ERROR_BORDER: 'var(--chart-danger-border)',
  INFO: 'var(--chart-info)',
  NEUTRAL: 'var(--chart-neutral)',
  MUTED: 'var(--chart-muted)',
  LIGHT: 'var(--chart-light)',
  LIGHT_BORDER: 'var(--chart-light-border)',
  REST: 'var(--chart-rest)',
  COVERED: 'var(--chart-covered)',
  COVERED_BORDER: 'var(--chart-covered-border)',
  UNCOVERED: 'var(--chart-uncovered)',
  UNCOVERED_BORDER: 'var(--chart-uncovered-border)',
  EMPTY: 'var(--chart-empty)',
  EVENTS: 'var(--chart-events)',
  ALERTS: 'var(--chart-alerts)',
  POLICIES: 'var(--chart-policies)',
  DOCUMENTS: 'var(--chart-documents)',
  EVIDENCES: 'var(--chart-evidences)',
} as const

export const SEVERITY_CHART_COLORS = {
  CRITICAL: 'var(--severity-critical-chart)',
  HIGH: 'var(--severity-high-chart)',
  MEDIUM: 'var(--severity-medium-chart)',
  LOW: 'var(--severity-low-chart)',
} as const

export const SEVERITY_CHART_BORDERS = {
  CRITICAL: 'var(--severity-critical-chart-border)',
  HIGH: 'var(--severity-high-chart-border)',
  MEDIUM: 'var(--severity-medium-chart-border)',
  LOW: 'var(--severity-low-chart-border)',
} as const

export const RISK_CHART_COLORS = {
  LOW: 'var(--risk-low-chart)',
  MEDIUM: 'var(--risk-medium-chart)',
  HIGH: 'var(--risk-high-chart)',
} as const

export const RISK_CHART_BORDERS = {
  LOW: 'var(--risk-low-chart-border)',
  MEDIUM: 'var(--risk-medium-chart-border)',
  HIGH: 'var(--risk-high-chart-border)',
} as const

export const PLATFORM_CHART_COLORS = {
  MACOS: 'var(--chart-macos)',
  WINDOWS: 'var(--chart-windows)',
  LINUX: 'var(--chart-linux)',
  IOS: 'var(--chart-ios)',
  ANDROID: 'var(--chart-android)',
} as const

export const PLATFORM_CHART_BORDERS = {
  MACOS: 'var(--chart-macos-border)',
  WINDOWS: 'var(--chart-windows-border)',
  LINUX: 'var(--chart-linux-border)',
  IOS: 'var(--chart-ios-border)',
  ANDROID: 'var(--chart-android-border)',
} as const

export const CHART_SERIES_COLORS = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
] as const

export const AUTH_EVENTS_CHART_COLORS = [
  'var(--chart-auth-1)',
  'var(--chart-auth-2)',
  'var(--chart-auth-3)',
  'var(--chart-auth-4)',
  'var(--chart-auth-5)',
] as const

export const MITRE_CHART_COLORS = [
  'var(--chart-mitre-1)',
  'var(--chart-mitre-2)',
  'var(--chart-mitre-3)',
  'var(--chart-mitre-4)',
  'var(--chart-mitre-5)',
  'var(--chart-mitre-6)',
  'var(--chart-mitre-7)',
  'var(--chart-mitre-8)',
  'var(--chart-mitre-9)',
  'var(--chart-mitre-10)',
] as const

export const MITRE_CHART_BORDERS = [
  'var(--chart-mitre-1-border)',
  'var(--chart-mitre-2-border)',
  'var(--chart-mitre-3-border)',
  'var(--chart-mitre-4-border)',
  'var(--chart-mitre-5-border)',
  'var(--chart-mitre-6-border)',
  'var(--chart-mitre-7-border)',
  'var(--chart-mitre-8-border)',
  'var(--chart-mitre-9-border)',
  'var(--chart-mitre-10-border)',
] as const

// Collaborative cursors and their name labels: dark enough for white label text
// on a white page, and distinguishable from each other.
export const COLLAB_CURSOR_COLORS = [
  '#2563eb', // tailwind blue-600
  '#c2410c', // tailwind orange-700
  '#15803d', // tailwind green-700
  '#7c3aed', // tailwind violet-600
  '#be123c', // tailwind rose-700
  '#0f766e', // tailwind teal-700
  '#a16207', // tailwind yellow-700
  '#4f46e5', // tailwind indigo-600
] as const
